import { gql } from '@reverbdotcom/commons/src/gql';
import {
  Item2AvailableActionsData,
  core_apimessages_UserActionName,
  core_apimessages_UserActionStatus,
} from '@reverbdotcom/commons/src/gql/graphql';
import { trackEvent, MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { getItem } from '../local_storage_utils';

export function trackDismissUserActionEvent(componentName: string, userActionName: core_apimessages_UserActionName) {
  trackEvent({
    eventName: MParticleEventName.ClickedComponent,
    componentName: componentName,
    interactionType: userActionName,
  });
}

export function isLoggedOutUserActionAvailable(userActionName: core_apimessages_UserActionName) {
  if (userActionName && USER_ACTION_LOCAL_STORAGE_KEYS[userActionName]) {
    return !getItem(USER_ACTION_LOCAL_STORAGE_KEYS[userActionName]);
  } else {
    return false;
  }
}

function actionAvailable(userActionName: string, availableActions: Item2AvailableActionsData.Fragment[]): boolean {
  return availableActions?.some(action => action.name === userActionName && action.status === core_apimessages_UserActionStatus.AVAILABLE);
}

export function isUserActionAvailable(userActionName: string, availableActions: Item2AvailableActionsData.Fragment[], isMobile = false): boolean {
  if (availableActions && userActionName) {
    if (isMobile && userActionName === core_apimessages_UserActionName.SAVE_SEARCH_EDUCATION_CALLOUT) {
      // we don't want to overload the user with callouts on marketplace, so let's give
      // priority to the great value filter callout on mweb
      const saveSearchEducationAvailable = actionAvailable(core_apimessages_UserActionName.SAVE_SEARCH_EDUCATION_CALLOUT, availableActions);
      const greatValueFilterCalloutAvailable = actionAvailable(core_apimessages_UserActionName.GREAT_VALUE_FILTER_CALLOUT, availableActions);

      return saveSearchEducationAvailable && !greatValueFilterCalloutAvailable;
    }

    return availableActions?.some(action => action.name === userActionName && action.status === core_apimessages_UserActionStatus.AVAILABLE);
  } else {
    return false;
  }
}

export const USER_ACTION_LOCAL_STORAGE_KEYS = {
  [core_apimessages_UserActionName.GREAT_VALUE_FILTER_CALLOUT]: 'great_value_filter_callout_seen_at',
};

const USER_ACTION_NAMES = [
  core_apimessages_UserActionName.CUSTOMIZE_FEED_EDUCATION_CALLOUT,
  core_apimessages_UserActionName.SAVE_SHOP_EDUCATION_CALLOUT,
  core_apimessages_UserActionName.SAVE_SEARCH_EDUCATION_CALLOUT,
  core_apimessages_UserActionName.GREAT_VALUE_FILTER_CALLOUT,
  core_apimessages_UserActionName.FAVORITES_TO_SAVED_REBRAND_CALLOUT,
  core_apimessages_UserActionName.ORGANIZE_YOUR_COLLECTION_CALLOUT,
] as const;

type ActionNameToI18nPrefix = {
  [key in typeof USER_ACTION_NAMES[number]]: string;
};

export const AVAILABLE_ACTION_NAME_TO_I18N_PREFIX: ActionNameToI18nPrefix = {
  [core_apimessages_UserActionName.CUSTOMIZE_FEED_EDUCATION_CALLOUT]: 'discovery.favorites.callouts.customizeFeedEducation',
  [core_apimessages_UserActionName.SAVE_SHOP_EDUCATION_CALLOUT]: 'discovery.favorites.callouts.saveShopEducation',
  [core_apimessages_UserActionName.FAVORITES_TO_SAVED_REBRAND_CALLOUT]: 'discovery.favorites.callouts.favoritesToSavedRebrand',
  [core_apimessages_UserActionName.SAVE_SEARCH_EDUCATION_CALLOUT]: 'discovery.favorites.callouts.saveSearchEducation',
  [core_apimessages_UserActionName.GREAT_VALUE_FILTER_CALLOUT]: 'commons.marketplaceFilters.greatValueCallout',
  [core_apimessages_UserActionName.ORGANIZE_YOUR_COLLECTION_CALLOUT]: 'discovery.gearCollections.index.listsCallout',
};


export const availableUserActionsQuery = gql(`
  query User_Available_Actions(
    $loggedOut: Boolean!
  ){
    me @skip(if: $loggedOut) {
      _id
      ...CreateUserActionMeData
      availableActions {
        ...Item2AvailableActionsData
      }
    }
  }
`);
